/* ASK BRILL CHATS (supabase/0042, her ask 2026-10-20).
 *
 * "Where do the conversations go and how do you rename them — that's how it
 * gets to be a full business partner." Until now Ask Anything was ONE endless
 * thread, and Brill read only its last 40 messages, so it forgot the start of
 * its own conversation.
 *
 * A chat is a NAME in `ask_brill_chats`. Its messages stay in `messages`, told
 * apart by `conversation_id`, which every message has always carried:
 *   - the old thread is `<user>-chat`  → shown as "Earlier conversations"
 *   - a new chat is `<user>-chat-<id>`
 * Nothing already written moves.
 *
 * A chat row is made when she sends its FIRST message, named from that message
 * (`titleFrom`). There is never an empty chat in the list, and naming it costs
 * no call to Brill.
 *
 * ONLY Ask Anything (module_key "chat"). Program FAQs, Mindset Support,
 * Testimonials and every Accelerator module keep their single thread: locking
 * reads those threads.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSupabase } from "../supabaseClient";

/** What kind of chat it is, saved under its own module_key. 0043 adds `mode` to the table.
 *
 *  THE COACHES (her call, 2026-10-22): "Get coached: Mindset, Story, Business,
 *  Sales, Social" replaced "I'm stuck on something" on the new-chat screen.
 *  Each `coach` has its own instructions in api/_lib/prompts.js (MODULE_GUIDES,
 *  under its moduleKey) and reads its slice of her foundation (CONTEXT_SCOPES
 *  in utils/momentumContext.js). `chip` is the word on the button; `placeholder`
 *  is the coach's first question, shown in the box once she picks it. Ask
 *  Anything and the program FAQs still run on general Brill — FAQs has no facts
 *  about the program to give it, which is still open.
 *  `socialmedia`, not `social`: "social" is already My Visibility's module id. */
export const MODES = {
  chat: { moduleKey: "chat", label: "Ask Anything" },
  faq: { moduleKey: "chat_faq", label: "About the program", placeholder: "Ask anything about how the program works…" },
  mindset: { moduleKey: "chat_mindset", label: "Mindset coach", chip: "Mindset", coach: true, placeholder: "What's weighing on you right now?" },
  story: { moduleKey: "chat_story", label: "Story coach", chip: "Story", coach: true, placeholder: "Tell me the moment. The raw version is fine." },
  business: { moduleKey: "chat_business", label: "Business coach", chip: "Business", coach: true, placeholder: "What's taking your time that shouldn't need you?" },
  sales: { moduleKey: "chat_sales", label: "Sales coach", chip: "Sales", coach: true, placeholder: "Who are you about to talk to, and about which offer?" },
  socialmedia: { moduleKey: "chat_socialmedia", label: "Social coach", chip: "Social", coach: true, placeholder: "Which platform, and what isn't working?" },
};
export const COACHES = Object.entries(MODES).filter(([, m]) => m.coach).map(([mode, m]) => ({ mode, ...m }));
export const CHAT_MODULE_KEYS = Object.values(MODES).map((m) => m.moduleKey);
/* ChatPanel's `chatTab` calls Ask Anything "general"; every other kind is its mode. */
const isKind = (m) => m !== "chat" && Object.prototype.hasOwnProperty.call(MODES, m);
export const modeOfTab = (tab) => (isKind(tab) ? tab : "chat");
export const tabOfMode = (mode) => (isKind(mode) ? mode : "general");

/** The key the list and the screen use for the old, unnamed thread. */
export const EARLIER = "earlier";
export const EARLIER_TITLE = "Earlier conversations";
export const EARLIER_NOTE = "Everything from before chats had names";
export const NOT_RUN = "Chats can't be kept yet — the database change (0042) hasn't been run. Ask Brill still works as one conversation.";

const TITLE_WORDS = 7;
const TITLE_CHARS = 60;

/** The conversation_id a chat's messages are saved under. EARLIER is the old thread. */
export function conversationIdFor(userId, chatKey) {
  if (!userId) return null;
  return chatKey && chatKey !== EARLIER ? `${userId}-chat-${chatKey}` : `${userId}-chat`;
}

/** Which chat a stored message belongs to: its id, or EARLIER for the old thread. */
export function chatKeyOf(conversationId, userId) {
  const prefix = `${userId}-chat-`;
  return typeof conversationId === "string" && conversationId.startsWith(prefix)
    ? conversationId.slice(prefix.length)
    : EARLIER;
}

/** A chat's name, from the first thing she asked: its first few words, as she wrote them. */
export function titleFrom(text) {
  const words = String(text || "").replace(/\s+/g, " ").trim().split(" ").slice(0, TITLE_WORDS).join(" ");
  let t = words.replace(/[\s?.!,:;…-]+$/u, "");
  if (t.length > TITLE_CHARS) t = t.slice(0, TITLE_CHARS).replace(/\s+\S*$/, "");
  if (!t) return "New chat";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };

/** The list's groups, newest first: Today, This week (the six days before), Earlier.
 *  Empty groups are left out. `chats` are rows with `updated_at`. */
export function groupChats(chats = [], now = new Date()) {
  const today = startOfDay(now);
  const weekStart = today - 6 * DAY;
  const groups = [
    { label: "Today", chats: [] },
    { label: "This week", chats: [] },
    { label: "Earlier", chats: [] },
  ];
  [...chats]
    .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")))
    .forEach((c) => {
      const t = new Date(c.updated_at || c.created_at || 0).getTime();
      const g = t >= today ? 0 : t >= weekStart ? 1 : 2;
      groups[g].chats.push(c);
    });
  return groups.filter((g) => g.chats.length);
}

/** Split her stored Ask Anything messages into threads: { [chatKey]: [messages] }. */
export function splitThreads(rows = [], userId) {
  const out = {};
  rows.forEach((r) => {
    const key = chatKeyOf(r.conversation_id, userId);
    (out[key] = out[key] || []).push(r);
  });
  return out;
}

export const missingTable = (error) => /ask_brill_chats|does not exist|schema cache/i.test(error?.message || "");

const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);

/**
 * The chat list. Loaded once when she signs in; every change is written as she
 * makes it. `problem` is a sentence for the screen (amber), never silence —
 * "Saved" and a write that never happened look identical otherwise.
 */
export function useAskBrillChats(userId) {
  const getSb = useSupabase();
  const sbRef = useRef(getSb);
  sbRef.current = getSb;
  const [chats, setChats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [available, setAvailable] = useState(true);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    let gone = false;
    if (!userId) return undefined;
    (async () => {
      try {
        const sb = await sbRef.current();
        const { data, error } = await sb.from("ask_brill_chats")
          .select("*")
          .eq("clerk_user_id", userId)
          .order("updated_at", { ascending: false });
        if (gone) return;
        if (error) {
          if (missingTable(error)) { setAvailable(false); setProblem(NOT_RUN); }
          else setProblem("Your chats couldn't be loaded. Refresh to try again.");
        } else {
          setChats(data || []);
        }
      } catch {
        if (!gone) setProblem("Your chats couldn't be loaded. Refresh to try again.");
      } finally {
        if (!gone) setLoaded(true);
      }
    })();
    return () => { gone = true; };
  }, [userId]);

  const write = useCallback(async (fn, failSentence) => {
    try {
      const sb = await sbRef.current();
      const { error } = await fn(sb);
      if (error) setProblem(missingTable(error) ? NOT_RUN : failSentence);
      else setProblem("");
      return !error;
    } catch {
      setProblem(failSentence);
      return false;
    }
  }, []);

  /** A new chat named from her first message. Returns its id at once; the row is written behind it. */
  const start = useCallback((firstMessage, mode = "chat") => {
    const at = new Date().toISOString();
    const row = { id: newId(), title: titleFrom(firstMessage), mode, created_at: at, updated_at: at };
    setChats((prev) => [row, ...prev]);
    /* `mode` is sent only when it is not the default, so an Ask Anything chat
       still saves before 0043 has run. */
    const { mode: m, ...base } = row;
    const insert = m === "chat" ? base : row;
    write((sb) => sb.from("ask_brill_chats").insert({ ...insert, clerk_user_id: userId }),
      m === "chat"
        ? "This chat's name didn't save. Its messages are kept, but it may not show in the list after a refresh."
        : "This chat didn't save as its own kind — the database change (0043) may not have been run.");
    return row.id;
  }, [userId, write]);

  /** A message went into this chat: it moves to the top of Today. */
  const touch = useCallback((id) => {
    if (!id || id === EARLIER) return;
    const at = new Date().toISOString();
    setChats((prev) => prev.map((c) => (c.id === id ? { ...c, updated_at: at } : c)));
    write((sb) => sb.from("ask_brill_chats").update({ updated_at: at }).eq("id", id), "");
  }, [write]);

  const rename = useCallback((id, title) => {
    const t = String(title || "").trim();
    if (!id || id === EARLIER || !t) return;
    setChats((prev) => prev.map((c) => (c.id === id ? { ...c, title: t } : c)));
    write((sb) => sb.from("ask_brill_chats").update({ title: t }).eq("id", id),
      "The new name didn't save. Try again in a moment.");
  }, [write]);

  /** Deletes the chat AND its messages. EARLIER deletes the old thread's messages. */
  const remove = useCallback(async (id) => {
    if (!id || !userId) return false;
    /* The old thread is everything in Ask Anything that is NOT a named chat —
       including rows from before conversation_id was written, which are null. */
    const okMsgs = await write((sb) => {
      const q = sb.from("messages").delete().eq("clerk_user_id", userId);
      return id === EARLIER
        ? q.eq("module_key", "chat").or(`conversation_id.is.null,conversation_id.not.like.${userId}-chat-*`)
        : q.in("module_key", CHAT_MODULE_KEYS).eq("conversation_id", conversationIdFor(userId, id));
    }, "That chat couldn't be deleted. Try again in a moment.");
    if (!okMsgs) return false;
    if (id !== EARLIER) {
      const okRow = await write((sb) => sb.from("ask_brill_chats").delete().eq("id", id),
        "That chat couldn't be deleted. Try again in a moment.");
      if (!okRow) return false;
      setChats((prev) => prev.filter((c) => c.id !== id));
    }
    return true;
  }, [userId, write]);

  /** Erase-everything has deleted the rows already; the list follows. */
  const clear = useCallback(() => setChats([]), []);

  return { chats, loaded, available, problem, start, touch, rename, remove, clear };
}
